import { useCallback } from 'react';
import { DefaultValues, Mode, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  FormZodParsed,
  FormZodSchema,
  FormZodValues,
} from '@sib/ui/types';
import { useDialogStore } from '@spendbrain/common/stores';

export interface UseBaseDialogFormProps<
  TSchema extends FormZodSchema,
  TReturnValues,
> {
  onClose?: () => void;
  defaultValues?: DefaultValues<FormZodValues<TSchema>>;
  validationSchema: TSchema;
  onSubmit: (
    values: FormZodParsed<TSchema>
  ) => Promise<void> | Promise<TReturnValues> | void;
  mode?: Mode;
}

export const useBaseDialogForm = <
  TSchema extends FormZodSchema,
  TReturnValues,
>({
  onClose,
  defaultValues,
  validationSchema,
  onSubmit: onSubmitExternal,
  mode = 'onChange',
}: UseBaseDialogFormProps<TSchema, TReturnValues>) => {
  const { close: closeDialog, setClosable } = useDialogStore();

  const formProps = useForm<
    FormZodValues<TSchema>,
    null,
    FormZodParsed<TSchema>
  >({
    defaultValues,
    mode,
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = useCallback(
    async (values: FormZodParsed<TSchema>) => {
      setClosable(false);
      await onSubmitExternal(values);
    },
    [onSubmitExternal, setClosable]
  );

  const onFormClose = useCallback(() => {
    onClose?.();
    closeDialog();
    formProps.reset();
  }, [onClose, formProps, closeDialog]);

  return {
    formProps,
    onSubmit,
    onFormClose,
  };
};
